import * as React from "react";
import { useNavigate } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CategoryOperations } from "../module/CategoryOperations";
import { DataList, Field, PageHeader, SiteBandwidthPill, StatusPill, useBandwidthCoverage, type DataListColumn } from "@ozpanel/kit";
import { DelegationCell, isDelegationFault, severityRank, type DelegationRow } from "./delegation";
import { Panel, Section, StateView, Tabs, TabsContent, TabsList, TabsTrigger, useAuth, useToast, Button, ConfirmDialog } from "@ozpanel/ui";

// Web vhosts (Caddy) and DNS delegation. Shapes read from a live node.

interface Vhost {
  id: number;
  domain: string;
  tenant_id: number;
  tenant_name: string;
  tenant_user: string;
  // An OBJECT, not a name: {name, candidates, running_on, enabled}. Typed as a string it rendered
  // "[object Object]" in the Serves column, which is what an operator saw first on the account
  // console's copy of this same mistake.
  app?: { name?: string; running_on?: string[] | null } | null;
  mode: string;
  enabled: boolean;
  served_here: boolean;
  dns_resolvable: boolean;
  tls_mode: string;
  tls_pending: boolean;
  created_at: string;
}
interface DnsDomain extends DelegationRow {
  // `binding` is an OBJECT — {fqdn, mode, provider, auto_sync} — not the domain string. Typed as a
  // string, the Domain column rendered "[object Object]" AND getRowKey returned the same value for
  // every row, so React saw one repeated key. `mismatch` is a string explaining the mismatch, not
  // a boolean; truthiness happened to work, which is why it went unnoticed.
  binding?: { fqdn?: string; mode?: string; provider?: string; auto_sync?: boolean } | null;
  detected_provider?: string;
  live_nameservers?: string[] | null;
  mismatch?: string;
}

function VhostsTab() {
  const navigate = useNavigate();
  const { client } = useAuth();
  const { notify } = useToast();
  const qc = useQueryClient();
  const [deleting, setDeleting] = React.useState<Vhost | null>(null);
  const q = useQuery({
    queryKey: ["caddy-vhosts"],
    queryFn: () => client.apiFetch<{ items: Vhost[] | null; total: number }>("/caddy/vhosts"),
  });
  const coverage = useBandwidthCoverage();

  const columns: DataListColumn<Vhost>[] = [
    { key: "domain", label: "Domain", sortable: true },
    { key: "tenant_name", label: "Account", sortable: true, value: (v) => v.tenant_name || "" },
    {
      key: "app", label: "Serves", sortable: true, value: (v) => v.app?.name || v.mode || "",
      render: (v) =>
        v.app?.name ? (
          <span>
            <span className="font-mono text-[12.5px]">{v.app.name}</span>
            {v.app.running_on?.length ? (
              <span className="ml-2 text-[11.5px] text-muted">on {v.app.running_on.join(", ")}</span>
            ) : (
              <span className="ml-2 text-[11.5px] text-warn">not running</span>
            )}
          </span>
        ) : (
          <span className="text-muted">{v.mode || "—"}</span>
        ),
    },
    {
      // THREE separate facts that a single "enabled" badge would collapse. A vhost can be
      // enabled, not served by this node, and pointing at a name that does not resolve — each
      // breaks the site in a different way and each has a different fix.
      key: "enabled",
      label: "Enabled",
      sortable: true,
      value: (v) => (v.enabled ? "yes" : "no"),
      render: (v) => <StatusPill tone={v.enabled ? "ok" : "warn"}>{v.enabled ? "enabled" : "disabled"}</StatusPill>,
    },
    {
      // Whether this site's traffic is being counted: web bandwidth comes from Caddy's per-host
      // metrics on each node, so a node where they are not in force serves the site uncounted.
      key: "traffic",
      label: "Traffic",
      render: () => <SiteBandwidthPill coverage={coverage.data} />,
    },
    {
      key: "served_here",
      label: "This node",
      sortable: true,
      value: (v) => (v.served_here ? "yes" : "no"),
      render: (v) =>
        v.served_here ? (
          <StatusPill tone="ok">served here</StatusPill>
        ) : (
          <StatusPill tone="neutral">served elsewhere</StatusPill>
        ),
    },
    {
      // The server field means "resolves to THIS node" (ipMatchesNode), not "resolves". Labelling
      // it "does not resolve" said a name was broken when it resolves perfectly and points at the
      // node that is supposed to answer it: appnode11.oztag.click is pinned to node11, and on
      // node10 this column called it a DNS failure in red. For a correctly pinned app that made
      // three columns — served elsewhere, does not resolve, certificate held — all read as faults
      // while describing the intended state exactly.
      key: "dns_resolvable",
      label: "Points here",
      sortable: true,
      value: (v) => (v.dns_resolvable ? "yes" : "no"),
      render: (v) =>
        v.dns_resolvable ? (
          <StatusPill tone="ok">points here</StatusPill>
        ) : v.served_here === false ? (
          // Not a fault: the app runs elsewhere, so the name pointing elsewhere is correct.
          <span className="text-[11.5px] text-muted" title="This name resolves — to the node that runs it.">
            points at its own node
          </span>
        ) : (
          // Served HERE but the name does not arrive here: that is a real fault.
          <StatusPill tone="bad">not pointing here</StatusPill>
        ),
    },
    {
      key: "tls",
      label: "TLS",
      sortable: true,
      value: (v) => v.tls_mode || "",
      // tls_pending is the ACME hold — a per-node fact. A name held here is served a
      // self-signed page unless the cluster already has a real certificate for it, so
      // "pending" is not a transient nicety, it is what a browser will complain about.
      render: (v) => (
        <span className="flex items-center gap-1.5">
          <span className="font-mono text-[12px]">{v.tls_mode || "—"}</span>
          {v.tls_pending && <StatusPill tone="warn">certificate held</StatusPill>}
        </span>
      ),
    },
  ];

  return (
    <>
    <Section title="Web vhosts" hint={q.data ? `${q.data.total}` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <DataList
          items={q.data?.items ?? []}
          columns={columns}
          getRowKey={(v) => String(v.id)}
          // Every list that leads somewhere says so, with a control rather than only a clickable
          // row — a bare hover tint hid an entire screen once already.
          onRowClick={(v) => navigate(`/web/${encodeURIComponent(v.domain)}`)}
          rowActions={(v) => (
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); navigate(`/web/${encodeURIComponent(v.domain)}`); }}>
                Open
              </Button>
              {/* Removing a site had no control anywhere in the console. DELETE /caddy/vhosts/
                  {domain} has always existed and nothing called it, so the only way to remove a
                  vhost was the API or the generic operations list. */}
              <Button
                size="sm"
                variant="danger"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleting(v);
                }}
              >
                Delete
              </Button>
            </div>
          )}
          searchable
          searchPlaceholder="Search domains, accounts…"
          loading={q.isLoading}
          error={q.error}
          errorContext="web vhosts"
          emptyMessage="No vhosts configured."
        />
      </div>
    </Section>

      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(o) => !o && setDeleting(null)}
        title={deleting ? `Delete ${deleting.domain}?` : ""}
        confirmLabel="Delete the site"
        destructive
        body={
          <div className="space-y-2 text-[12.5px] leading-relaxed">
            <p>
              Removes the vhost from this cluster: <span className="font-mono">{deleting?.domain}</span>{" "}
              stops being served on every node and its certificate is no longer renewed.
            </p>
            {/* What it does NOT remove is the part an operator has to know, because the reassuring
                half and the dangerous half are different. */}
            <p className="text-muted">
              The document root and its files are left where they are, and the DNS records are left
              alone — the name will keep resolving to a node that no longer answers for it.
            </p>
          </div>
        }
        onConfirm={() => {
          const v = deleting;
          setDeleting(null);
          if (!v) return;
          void client
            .apiFetch(`/caddy/vhosts/${encodeURIComponent(v.domain)}`, { method: "DELETE" })
            .then(() => {
              notify({ kind: "success", title: `${v.domain} removed` });
              void qc.invalidateQueries({ queryKey: ["caddy-vhosts"] });
            })
            .catch((e: unknown) =>
              notify({
                kind: "error",
                title: `Could not remove ${v.domain}`,
                body: e instanceof Error ? e.message : String(e),
              }),
            );
        }}
      />
    </>
  );
}

function DelegationTab() {
  const { client } = useAuth();
  const q = useQuery({
    queryKey: ["dns-domains"],
    queryFn: () => client.apiFetch<{ items: DnsDomain[] | null; total: number; note?: string }>("/dns/domains"),
  });

  const items = q.data?.items ?? [];
  // Only rows that need ACTION. This counted every row with any message, so the banner announced
  // "N zone(s) are not delegated to this platform" about zones this platform is serving.
  const mismatched = items.filter(isDelegationFault).length;

  const columns: DataListColumn<DnsDomain>[] = [
    {
      key: "binding", label: "Domain", sortable: true, value: (d) => d.binding?.fqdn || "",
      render: (d) => <span className="font-mono text-[12.5px]">{d.binding?.fqdn || "—"}</span>,
    },
    {
      key: "detected_provider",
      label: "Provider at the registrar",
      sortable: true,
      value: (d) => d.detected_provider || "",
      render: (d) => d.detected_provider || <span className="text-muted">unknown</span>,
    },
    {
      key: "live_nameservers",
      label: "Live nameservers",
      value: (d) => (d.live_nameservers ?? []).join(", "),
      render: (d) => <span className="font-mono text-[11.5px]">{(d.live_nameservers ?? []).join(", ") || "—"}</span>,
    },
    {
      key: "mismatch",
      label: "Delegation",
      sortable: true,
      value: (d) => severityRank(d.mismatch_severity),
      // A domain delegated somewhere else is the reason records "do not apply": the panel is
      // authoritative for a zone the internet is not being sent to. But that is only two of the
      // five states this column describes, and this used to paint all of them red — see
      // ./delegation for the measurement.
      render: (d) => <DelegationCell d={d} />,
    },
  ];

  return (
    <Section title="DNS delegation" hint={q.data ? `${q.data.total} zone(s)` : undefined} bodyClassName="p-0">
      <div className="p-4">
        <StateView loading={q.isLoading} error={q.error} errorContext="DNS delegation">
          <>
            {mismatched > 0 && (
              <Panel className="mb-3 border-[color-mix(in_oklab,var(--ozp-crit)_38%,transparent)] p-3 text-[12.5px]">
                <b>{mismatched} zone(s) are not delegated to this platform.</b> Records edited here are served by
                this node's authoritative DNS, but the registrar is sending the internet somewhere else — so every
                change appears to apply and nothing takes effect.
              </Panel>
            )}
            <DataList
              items={items}
              columns={columns}
              getRowKey={(d) => String(d.binding?.fqdn)}
              searchable
              searchPlaceholder="Search zones…"
              emptyMessage="No zones."
            />
            {q.data?.note && <p className="mt-3 text-[11.5px] leading-relaxed text-muted">{q.data.note}</p>}
          </>
        </StateView>
      </div>
    </Section>
  );
}

interface WelcomeSettings {
  brand_name?: string;
  brand_url?: string;
  headline?: string;
  body?: string;
  template?: string;
}
interface WelcomeView {
  settings?: WelcomeSettings;
  /** The shipped values a blank field falls back to — shown as placeholders so "empty" reads as
   *  "the default", not as "nothing". */
  defaults?: WelcomeSettings;
  node_host?: string;
  /** COUNTS, not prose. The server has always sent map[string]int from welcomeSummary() —
   *  {"total": n, "<state>": n, "<action>": n}. This was typed as a string and rendered
   *  directly, so React threw "Objects are not valid as a React child" and React Router's
   *  boundary replaced the ENTIRE /admin/web route with "Unexpected Application Error!" —
   *  taking the vhost table and the DNS table down with it, neither of which was at fault. */
  summary?: Record<string, number> | null;
  using_override?: boolean;
  sites?: { domain?: string; managed?: boolean }[] | null;
}

/** The page every domain serves before its owner puts anything there.
 *
 * The whole welcome module was unreachable: five routes, no console anywhere, so the page a new
 * customer sees first — the one carrying the host's name — could only be changed by calling the
 * API by hand. A blank field means "use the shipped default", which is why the defaults are shown
 * as placeholders rather than pre-filled: pre-filling them would store a copy, and the page would
 * then stop following the default when it changed. */
function WelcomePage() {
  const { client } = useAuth();
  const qc = useQueryClient();
  const { notify } = useToast();
  const q = useQuery({
    queryKey: ["caddy-welcome"],
    queryFn: () => client.apiFetch<WelcomeView>("/caddy/welcome"),
    retry: false,
  });
  const [draft, setDraft] = React.useState<WelcomeSettings | null>(null);
  const [busy, setBusy] = React.useState(false);
  const w = draft ?? q.data?.settings ?? {};
  const d = q.data?.defaults ?? {};

  const act = async (fn: () => Promise<unknown>, title: string) => {
    setBusy(true);
    try {
      await fn();
      setDraft(null);
      notify({ kind: "success", title });
      void qc.invalidateQueries({ queryKey: ["caddy-welcome"] });
    } catch (e) {
      notify({ kind: "error", title: "That did not work", body: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  // The placeholder is the SHIPPED DEFAULT, which is why it is not pre-filled: pre-filling stores
  // a copy and the page then stops following the default when it changes.
  const F = ({ k, label }: { k: keyof WelcomeSettings; label: string }) => (
    <Field label={label} value={String(w[k] ?? "")} placeholder={String(d[k] ?? "")} onChange={(v) => setDraft({ ...w, [k]: v })} />
  );

  return (
    <Section title="Default page for new sites" className="mt-6">
      <StateView loading={q.isLoading} error={q.error} errorContext="the welcome page">
        <div className="space-y-3">
          {q.data?.summary && Object.keys(q.data.summary).length > 0 && (
            <p className="max-w-3xl text-[12px] leading-relaxed text-muted">
              {(() => {
                const sm = q.data.summary;
                const total = sm.total ?? 0;
                const rest = Object.entries(sm)
                  .filter(([k, v]) => k !== "total" && v > 0)
                  .map(([k, v]) => `${v} ${k}`)
                  .join(", ");
                return `${total} site${total === 1 ? "" : "s"} would use this page${rest ? ` — ${rest}` : ""}.`;
              })()}
            </p>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            <F k="brand_name" label="Who is hosting this (printed on the page)" />
            <F k="brand_url" label="Link on that name (blank = plain text)" />
          </div>
          <F k="headline" label="One line under the domain name" />
          <p className="text-[11.5px] leading-relaxed text-muted">
            Leaving a field empty keeps the shipped default shown as the placeholder — so it keeps
            following that default rather than storing a copy of today's wording.
            {q.data?.node_host ? ` This node prints itself as ${q.data.node_host}.` : ""}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              disabled={busy || draft === null}
              onClick={() => void act(() => client.apiFetch("/caddy/welcome", { method: "PUT", body: JSON.stringify(w) }), "Welcome page saved")}
            >
              {busy ? "Saving…" : "Save"}
            </Button>
            {q.data?.using_override && (
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => void act(() => client.apiFetch("/caddy/welcome", { method: "DELETE" }), "Restored the shipped page")}
              >
                Restore the shipped wording
              </Button>
            )}
            {draft !== null && (
              <Button size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Discard
              </Button>
            )}
          </div>
        </div>
      </StateView>
    </Section>
  );
}

export function VhostsScreen() {
  return (
    <div>
      <PageHeader title="Web & DNS" subtitle="Vhosts served by this node, and whether their zones are delegated here" />
      <WelcomePage />
      <Tabs defaultValue="vhosts">
        <TabsList>
          <TabsTrigger value="vhosts">Vhosts</TabsTrigger>
          <TabsTrigger value="delegation">DNS delegation</TabsTrigger>
        </TabsList>
        <TabsContent value="vhosts">
          <VhostsTab />
        </TabsContent>
        <TabsContent value="delegation">
          <DelegationTab />
        </TabsContent>
      </Tabs>
      <CategoryOperations category="web" title="Website & DNS operations" hint="Vhosts, certificates, DNS zones and the registrar bindings." />

    </div>
  );
}
